<template>
  <div class="WorldMap">
    <!-- <img src="../assets/svg/Map.png" height="670" /> -->
    <MapBox
      class="MapBox__wrapper" 
      :accessToken="accessToken"
      :mapStyle="mapStyle"
      :options="options"
      @load="onMapLoaded" />
  </div>
</template>

<script>
// import { MglMap } from 'vue-mapbox'
import MapBox from '@/components/MapBox'
import mapboxgl from 'mapbox-gl'

export default {
  name: "WorldMap",
  components: {
    MapBox
  },
  props: {
    projects: {
      type: String,
      default: ''
    },
    filter: {
      type: String,
      default: null
    }
  },
  $map: null, // non-reactive on purpose
  data() {
    return {
      accessToken: 'pk.eyJ1Ijoiam91cm5leXdvcmtzIiwiYSI6ImNqemlldmNqcTE1MzEzZG81YmV2dGhwdzYifQ.mZNXE5qdxs4z47hxNyisIA',
      mapStyle: 'mapbox://styles/journeyworks/cjzifosxp00bd1cn7x0o1fcff',
      maxZoom: 7
    }
  },
  computed: {
        /**
     * Determine the map bounds based on the coordinates of the locations being shown
     */
    bounds() {
      return this.parsedProjects.reduce(
        (bounds, location) => {
          console.log(location)
          return bounds.extend([
            Array.isArray(location.lng) ? location.lng[0] : location.lng, 
            Array.isArray(location.lat) ? location.lat[0] : location.lat
          ])
        },
        new mapboxgl.LngLatBounds()
      )
    },
    /**
     * Supplies the mapbox options.
     */
    options() {
      return { bounds: this.bounds, fitBoundsOptions: { padding: 50 } }
    },
    parsedProjects() {
      return JSON.parse(this.projects)
    },
    parsedFilter() {
      return JSON.parse(this.filter)
    },
    // Simply setting a filter is not possible... 
    // See: https://github.com/mapbox/mapbox-gl-js/issues/2613
    filteredProjects() {
      let data = this.parsedProjects

      if (this.filter) {
        let filter = this.parsedFilter
        if (
          Array.isArray(filter) && 
          filter.length !== this.projects.length
        ) {
          data = data.filter(item => {
            return filter.includes(item.slug)
          })
        }
      }

      return data
        .map(project => {
          return {
            "type": "Feature",
            "geometry": {
              "type": "Point",
              "coordinates": [
                Array.isArray(project.lng) ? project.lng[0] : project.lng,
                Array.isArray(project.lat) ? project.lat[0] : project.lat
              ]
            },
            "properties": {
              slug: project.slug,
              name: project.name
            }
          }
        })
    }
  },
  watch: {
    filteredProjects(data) {
      if (data) {
        // Simply setting a filter is not possible... 
        // See: https://github.com/mapbox/mapbox-gl-js/issues/2613
        this.$map.getSource('projects').setData({
          "name": "Projects",
          "type": "FeatureCollection",
          "features": data
        })
      }
    }
  },
  methods: {
    onMapLoaded(event) {
      // Note: a reference to the map has to be stored in a non-reactive manner.
      this.$map = event.map;

      event.map.setRenderWorldCopies(false);
      event.map.scrollZoom.disable();
      event.map.addControl(new mapboxgl.NavigationControl());

      // TODO: Cut off antarctica - in another way
      // event.map.setMaxBounds([[-179.9, -60], [179.9, 85]]);

      this.loadData()

      // https://docs.mapbox.com/mapbox-gl-js/example/filter-features-within-map-view/
      event.map.on('moveend', () => {
        let features = event.map.queryRenderedFeatures({ layers: ['hidden-projects'] })
        this.$emit('visible', { features })
      })

      this.addHoverPopup()
    },
    loadData() {

      let map = this.$map;
      
      // Add a new source from our GeoJSON data and set the
      // 'cluster' option to true. GL-JS will add the point_count property to your source data.
      map.addSource("projects", {
        type: "geojson",
        data: {
          "name": "Projects",
          "type": "FeatureCollection",
          "features": this.filteredProjects
        },
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
      });

      // Not visible. Used for filtering based on zoom
      map.addSource("hidden-projects", {
        type: "geojson",
        data: {
          "name": "HiddenProjects",
          "type": "FeatureCollection",
          "features": this.filteredProjects
        }
      });
      map.addLayer({
        id: 'hidden-projects',
        type: 'circle',
        source: 'hidden-projects',
        paint: {
          "circle-radius": 0
        }
      })
      
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "projects",
        filter: ["has", "point_count"],
        paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          // with three steps to implement three types of circles:
          //   * Blue, 20px circles when point count is less than 100
          //   * Yellow, 30px circles when point count is between 100 and 750
          //   * Pink, 40px circles when point count is greater than or equal to 750
          "circle-color": [
          "step",
          ["get", "point_count"],
            "#FF715B",
            3,
            "#FBB13C",
            6,
            "#1ECDA4"
          ],
          "circle-radius": 20
        }
      });
      
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "projects",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12
        }
      });
      
      map.addLayer({
        id: "unclustered-point",
        type: "symbol",
        source: "projects",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "symbol-placement": "point",
          "symbol-z-order": "source",
          "icon-allow-overlap": true,
          "icon-image": "IconMapPin", 
          "icon-size": 1.2
        },
      });
      
      // inspect a cluster on click
      map.on('click', 'clusters', function (e) {
        var features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        var clusterId = features[0].properties.cluster_id;
        map.getSource('projects').getClusterExpansionZoom(clusterId, function (err, zoom) {
          if (err)
            return;
          
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          });
        });
      });
      map.on('click', 'unclustered-point', (e) => {
        let features = map.queryRenderedFeatures(e.point, { layers: ['unclustered-point']})
        
        this.$emit('project', { slug: features[0].properties.slug })
        // let pointId = features[0].properties.point_id
      })
      
      map.on('mouseenter', 'clusters', function () {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'clusters', function () {
        map.getCanvas().style.cursor = '';
      });
      map.on('mouseenter', 'unclustered-point', function () {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'unclustered-point', function () {
        map.getCanvas().style.cursor = '';
      });
    },
    addHoverPopup() {
      // The name pop-up on hover
      let popup = new mapboxgl.Popup({
        closeButton: false
      })
      this.$map.on('mousemove', 'unclustered-point', (e) => {
        // Change the cursor style as a UI indicator.
        this.$map.getCanvas().style.cursor = 'pointer'
        
        // Populate the popup and set its coordinates based on the feature.
        let feature = e.features[0]
        popup.setLngLat(feature.geometry.coordinates)
          .setText(feature.properties.name)
          .addTo(this.$map)
      })
      this.$map.on('mouseleave', 'unclustered-point', () => {
        this.$map.getCanvas().style.cursor = ''
        popup.remove()
      })
    }
  },
}
</script>

<style>
@import 'mapbox.css';

.WorldMap {
  background-color: #354052;
  height: 670px;
  max-height: calc(90vh - 150px);
  width: 100%;
}
</style>